import { useEffect, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { RoleProvider } from "@/contexts/RoleContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/public/Index";
import TeacherProfilePage from "./pages/public/TeacherProfilePage";
import StudentDashboard from "./pages/student/StudentDashboard";
import TeacherDashboard from "./pages/teacher/TeacherDashboard";
import MessagesPage from "./pages/student/MessagesPage";

import AboutPage from "./pages/public/AboutPage";
import PricingPage from "./pages/public/PricingPage";
import ContactPage from "./pages/public/ContactPage";
import TeacherProfileSetup from "./pages/teacher/TeacherProfileSetup";
import StudentProfileSetup from "./pages/student/StudentProfileSetup";
import SupportDashboard from "./pages/common/SupportDashboard";
import OfferingSearchRoute from "./pages/public/OfferingSearchRoute";
import AllSubjectsPage from "./pages/public/AllSubjectsPage";
import MyInquiriesPage from "./pages/student/MyInquiriesPage";
import MyGoalPage from "./pages/student/MyGoalPage";
import StudentCoinsPage from "./pages/student/StudentCoinsPage";
import BuyCoinsPage from "./pages/student/BuyCoinsPage";
import MyProfilePage from "./pages/student/MyProfilePage";
import SavedTeachersPage from "./pages/student/SavedTeachersPage";
import InquiryDetailPage from "./pages/student/InquiryDetailPage";
import TeacherInquiriesPage from "./pages/teacher/TeacherInquiriesPage";
import TeacherOfferingsPage from "./pages/teacher/TeacherOfferingsPage";
import CreateOfferingPage from "./pages/teacher/CreateOfferingPage";
import CreateOfferingWizard from "./pages/teacher/CreateOfferingWizard";
import EditOfferingPage from "./pages/teacher/EditOfferingPage";
import ViewOfferingPage from "./pages/teacher/ViewOfferingPage";
import OfferingDetailPage from "./pages/public/OfferingDetailPage";
import PublicOfferingPage from "./pages/public/PublicOfferingPage";
import HowItWorksPage from "./pages/public/HowItWorksPage";
import HowItWorksGoalsPage from "./pages/public/HowItWorksGoalsPage";
import HowItWorksMatchingPage from "./pages/public/HowItWorksMatchingPage";
import HowItWorksFirstSessionPage from "./pages/public/HowItWorksFirstSessionPage";
import HowItWorksApplyPage from "./pages/public/HowItWorksApplyPage";
import ExamCrashCoursesPage from "./pages/public/ExamCrashCoursesPage";
import BecomeOnlineTutorPage from "./pages/public/BecomeOnlineTutorPage";
import PrivateSessionsPage from "./pages/public/PrivateSessionsPage";
import GroupClassesPage from "./pages/public/GroupClassesPage";
import HowItWorksVerificationPage from "./pages/public/HowItWorksVerificationPage";
import HowItWorksEarningPage from "./pages/public/HowItWorksEarningPage";
import SendInquiryPage from "./pages/student/SendInquiryPage";
import GoalOnboardingPage from "./pages/GoalOnboardingPage";
import TeacherReviewsPage from "./pages/teacher/TeacherReviewsPage";
import TeacherAnalyticsPage from "./pages/teacher/TeacherAnalyticsPage";
import TeacherCoinsPage from "./pages/teacher/TeacherCoinsPage";
import TeacherBuyCoinsPage from "./pages/teacher/TeacherBuyCoinsPage";
import TeacherSubscriptionPage from "@/pages/teacher/TeacherSubscriptionPage";
import ClassesHubPage from './pages/teacher/ClassesHubPage';
import OfferingClassManagementPage from './pages/teacher/OfferingClassManagementPage';
import SessionDetailPage from './pages/teacher/SessionDetailPage';
import StudentMyClassesPage from './pages/student/StudentMyClassesPage';
import StudentClassDetailPage from './pages/student/StudentClassDetailPage';
import PrivacyPage from "./pages/public/PrivacyPage";
import TermsPage from "./pages/public/TermsPage";
import BlogPage from "./pages/public/BlogPage";
import BlogPostPage from "./pages/public/BlogPostPage";
import NotFound from "./pages/common/NotFound";
import InboxRedirect from "./pages/common/InboxRedirect";
import AccountDeactivated from "./pages/common/AccountDeactivated";
import SelectRole from "./pages/public/SelectRole";
import RoleSwitchPage from "./pages/common/RoleSwitchPage";
import { TeacherProfileProvider, useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { StudentProfileProvider } from "@/contexts/StudentProfileContext";

import OAuthCallback from "./components/auth/OAuthCallback";
import AuthGuard from "./components/auth/AuthGuard";
import RegistrationGateModal from "./components/RegistrationGateModal";
import LoginPage from "./pages/public/LoginPage";
import RegisterPage from "./pages/public/RegisterPage";
import ConfirmSignUpPage from "./pages/public/ConfirmSignUpPage";
import SettingsPage from "./pages/student/SettingsPage";
import DeletionConfirmationPage from "./pages/DeletionConfirmationPage";

const ConsentPendingPage = lazy(() => import('./pages/public/ConsentPendingPage'));
const ConsentFormPage = lazy(() => import('./pages/public/ConsentFormPage'));
const RegisterResumePage = lazy(() => import('./pages/public/RegisterResumePage'));
const ConsentWithdrawalPage = lazy(() => import('./pages/public/ConsentWithdrawalPage'));
const AgeGatePage = lazy(() => import('./pages/public/AgeGatePage'));
import { configureAmplify } from "./lib/amplifyConfig";
import { bootstrapE2EAuth } from "./lib/e2eAuth";
import { Authenticator } from "@aws-amplify/ui-react";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { HelpAssistant } from "@/components/help/HelpAssistant";
import { CookieConsentBanner } from "@/components/common/CookieConsentBanner";
import { DefaultHelmet } from "@/components/seo/DefaultHelmet";

configureAmplify();
// E2E-only: seed a synthetic authed session before React renders (no-op unless
// built with VITE_E2E_AUTH=true AND running on localhost). See src/lib/e2eAuth.ts.
bootstrapE2EAuth();

const queryClient = new QueryClient();

/** Bridges route changes to the teacher profile context for auto-refresh. */
function ProfileNavigationSync() {
  const location = useLocation();
  const { onNavigate } = useTeacherProfile();
  useEffect(() => { onNavigate(); }, [location.pathname, onNavigate]);
  return null;
}

/**
 * Legacy /search and /search-results → per-type search route, keeping the query
 * string. A bare <Navigate to="/search/one-to-one"> dropped it, so every
 * `/search?q=…` link (home/All Subjects cards, hero tags, goal onboarding)
 * landed on an empty, criteria-free search page.
 */
function LegacySearchRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  params.delete("tab");
  const path = tab === "group-coaching" ? "/search/group" : "/search/one-to-one";
  const qs = params.toString();
  return <Navigate to={qs ? `${path}?${qs}` : path} replace />;
}

/** Fallback while a lazy-loaded route's chunk is fetched. */
function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );
}

const App = () => (
  <>
  <DefaultHelmet />
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} themes={["light", "ocean", "student"]}>
  <Authenticator.Provider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
          <RoleProvider >
            <TeacherProfileProvider>
              <StudentProfileProvider>

              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ErrorBoundary>
                <ProfileNavigationSync />
                <RegistrationGateModal />
                <HelpAssistant />
                <CookieConsentBanner />
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/t/:handle/:slug" element={<PublicOfferingPage />} />
          <Route path="/t/:handle" element={<TeacherProfilePage />} />
          <Route path="/search" element={<LegacySearchRedirect />} />
          <Route path="/search-results" element={<LegacySearchRedirect />} />
          <Route path="/search/one-to-one" element={<OfferingSearchRoute offeringType="ONE_TO_ONE" />} />
          <Route path="/search/group" element={<OfferingSearchRoute offeringType="GROUP" />} />
          <Route path="/search/crash-course" element={<OfferingSearchRoute offeringType="CRASH_COURSE" />} />
          <Route path="/subjects" element={<AllSubjectsPage />} />
          <Route path="/student-dashboard" element={<AuthGuard><StudentDashboard /></AuthGuard>} />
          <Route path="/teacher-dashboard" element={<AuthGuard><TeacherDashboard /></AuthGuard>} />
          <Route path="/switch-role" element={<AuthGuard><RoleSwitchPage /></AuthGuard>} />
          <Route path="/messages" element={<AuthGuard><MessagesPage /></AuthGuard>} />

          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/teacher-profile-setup" element={<AuthGuard><TeacherProfileSetup /></AuthGuard>} />
          <Route path="/student-profile-setup" element={<AuthGuard><StudentProfileSetup /></AuthGuard>} />
          <Route path="/support-dashboard" element={<AuthGuard><SupportDashboard /></AuthGuard>} />
          {/* Public pricing: BMC Coins AND teacher tiers on one indexable URL; logged-in top-ups use /buy-coins. */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/buy-coins" element={<AuthGuard><BuyCoinsPage /></AuthGuard>} />
          <Route path="/my-inquiries" element={<AuthGuard><MyInquiriesPage /></AuthGuard>} />
          <Route path="/my-goal" element={<AuthGuard><MyGoalPage /></AuthGuard>} />
          <Route path="/student-coins" element={<AuthGuard><StudentCoinsPage /></AuthGuard>} />
          <Route path="/my-profile" element={<AuthGuard><MyProfilePage /></AuthGuard>} />
          <Route path="/saved" element={<AuthGuard><SavedTeachersPage /></AuthGuard>} />
          <Route path="/inquiry/:id" element={<AuthGuard><InquiryDetailPage /></AuthGuard>} />
          <Route path="/teacher-offerings" element={<AuthGuard><TeacherOfferingsPage /></AuthGuard>} />
          <Route path="/create-offering" element={<AuthGuard><CreateOfferingPage /></AuthGuard>} />
          <Route path="/create-offering/:type" element={<AuthGuard><CreateOfferingWizard /></AuthGuard>} />
          <Route path="/edit-offering/:id" element={<AuthGuard><CreateOfferingWizard /></AuthGuard>} />
          <Route path="/create-offering/:type/:id" element={<AuthGuard><CreateOfferingWizard /></AuthGuard>} />
          <Route path="/teacher-offering/:id" element={<AuthGuard><ViewOfferingPage /></AuthGuard>} />
          <Route path="/teacher-inquiries" element={<AuthGuard><TeacherInquiriesPage /></AuthGuard>} />
          {/* Legacy inquiry-email links — forward to the user's own inbox */}
          <Route path="/inbox" element={<AuthGuard><InboxRedirect /></AuthGuard>} />
          <Route path="/inbox/:threadId" element={<AuthGuard><InboxRedirect /></AuthGuard>} />
          <Route path="/offering/:id" element={<AuthGuard><OfferingDetailPage /></AuthGuard>} />
<Route path="/public/offering/:id" element={<PublicOfferingPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/how-it-works/goals" element={<HowItWorksGoalsPage />} />
          <Route path="/how-it-works/matching" element={<HowItWorksMatchingPage />} />
          <Route path="/how-it-works/first-session" element={<HowItWorksFirstSessionPage />} />
          <Route path="/how-it-works/apply" element={<HowItWorksApplyPage />} />
          <Route path="/exam-crash-courses" element={<ExamCrashCoursesPage />} />
          <Route path="/become-an-online-tutor" element={<BecomeOnlineTutorPage />} />
          <Route path="/private-tutoring-sessions" element={<PrivateSessionsPage />} />
          <Route path="/online-group-classes" element={<GroupClassesPage />} />
          <Route path="/how-it-works/verification" element={<HowItWorksVerificationPage />} />
          <Route path="/how-it-works/earning" element={<HowItWorksEarningPage />} />
          <Route path="/teacher/:id/inquiry" element={<SendInquiryPage />} />
          <Route path="/teacher-reviews" element={<AuthGuard><TeacherReviewsPage /></AuthGuard>} />
          <Route path="/teacher-analytics" element={<AuthGuard><TeacherAnalyticsPage /></AuthGuard>} />
          <Route path="/teacher-coins" element={<AuthGuard><TeacherCoinsPage /></AuthGuard>} />
          <Route path="/teacher-buy-coins" element={<AuthGuard><TeacherBuyCoinsPage /></AuthGuard>} />
          <Route path="/teacher-subscription" element={<AuthGuard><TeacherSubscriptionPage /></AuthGuard>} />
          <Route path="/teacher-classes" element={<AuthGuard><ClassesHubPage /></AuthGuard>} />
          <Route path="/teacher-classes/:offeringId" element={<AuthGuard><OfferingClassManagementPage /></AuthGuard>} />
          <Route path="/teacher-classes/:offeringId/sessions/:sessionId" element={<AuthGuard><SessionDetailPage /></AuthGuard>} />
          <Route path="/my-classes" element={<AuthGuard><StudentMyClassesPage /></AuthGuard>} />
          <Route path="/my-classes/:offeringId" element={<AuthGuard><StudentClassDetailPage /></AuthGuard>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/confirm-signup" element={<ConfirmSignUpPage />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/callback" element={<OAuthCallback />} />
          <Route path="/goal-onboarding" element={<AuthGuard><GoalOnboardingPage /></AuthGuard>} />
          <Route path="/select-role" element={<AuthGuard><SelectRole /></AuthGuard>} />
          <Route path="/settings" element={<AuthGuard><SettingsPage /></AuthGuard>} />
          {/* Public route — deletion token is the credential, no auth guard needed */}
          <Route path="/account/confirm-deletion" element={<DeletionConfirmationPage />} />
          {/* Unguarded — deactivated users can't pass profile checks */}
          <Route path="/account-deactivated" element={<AccountDeactivated />} />

          <Route path="/parental-consent-pending" element={<ConsentPendingPage />} />
          <Route path="/parental-consent" element={<ConsentFormPage />} />
          <Route path="/register/resume" element={<RegisterResumePage />} />
          <Route path="/consent-withdrawal" element={<ConsentWithdrawalPage />} />
          <Route path="/age-gate" element={<AgeGatePage />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
                </ErrorBoundary>
        </BrowserRouter>
              </StudentProfileProvider>
              </TeacherProfileProvider>
            </RoleProvider>
        </LanguageProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </Authenticator.Provider>
  </ThemeProvider>
  </>
);

export default App;
